// ---------------------------------------------------------------------------
// Blog Listing Grid Renderer
// ---------------------------------------------------------------------------

import { registerRenderer } from "../registry.js";
import type { RenderContext } from "../types.js";
import { attr, text, url } from "../escape.js";

interface BlogPost {
  title: string;
  excerpt?: string;
  featuredImage?: string;
  publishedAt?: string;
  authorName?: string;
  authorUrl?: string;
  category?: string;
  slug: string;
  readingTimeMinutes?: number;
}

interface BlogListingGridProps {
  heading?: string;
  posts: BlogPost[];
  columns?: number;
  showCategory?: boolean;
  showAuthor?: boolean;
  ctaText?: string;
  baseUrl?: string;
}

function renderBlogCard(
  post: BlogPost,
  baseUrl: string,
  ctaText: string,
  showCategory: boolean,
  showAuthor: boolean,
): string {
  const title = post.title ?? "";
  const href = `${baseUrl}/${post.slug ?? ""}`;

  const imageHtml = post.featuredImage
    ? `\n      <img src="${url(post.featuredImage)}" alt="${attr(title)}" class="ease-blog-card-image" loading="lazy">`
    : "";

  const categoryHtml = showCategory && post.category
    ? `\n      <span class="ease-blog-card-category">${text(post.category)}</span>`
    : "";

  const excerptHtml = post.excerpt
    ? `\n      <p class="ease-blog-card-excerpt">${text(post.excerpt)}</p>`
    : "";

  const dateHtml = post.publishedAt
    ? `<time class="ease-blog-card-date" datetime="${attr(post.publishedAt)}">${text(post.publishedAt)}</time>`
    : "";

  const authorHtml = showAuthor && post.authorName
    ? post.authorUrl
      ? `<a href="${url(post.authorUrl)}" class="ease-blog-card-author">${text(post.authorName)}</a>`
      : `<span class="ease-blog-card-author">${text(post.authorName)}</span>`
    : "";

  const readingHtml = post.readingTimeMinutes
    ? `<span class="ease-blog-card-reading-time">${text(String(post.readingTimeMinutes))} min read</span>`
    : "";

  const metaParts = [dateHtml, authorHtml, readingHtml].filter(Boolean);
  const footerHtml = metaParts.length > 0
    ? `\n      <footer class="ease-blog-card-footer">${metaParts.join(" &middot; ")}</footer>`
    : "";

  return `    <article class="ease-blog-card" aria-label="${attr(title)}">${imageHtml}${categoryHtml}
      <h3 class="ease-blog-card-title"><a href="${url(href)}">${text(title)}</a></h3>${excerptHtml}${footerHtml}
      <a href="${url(href)}" class="ease-blog-card-cta">${text(ctaText)}</a>
    </article>`;
}

function render(props: Record<string, unknown>, _ctx: RenderContext): string {
  const p = props as unknown as BlogListingGridProps;

  const heading = p.heading ?? "Latest Posts";
  const posts = Array.isArray(p.posts) ? p.posts : [];
  const columns = Math.max(1, Math.min(3, p.columns ?? 3));
  const showCategory = p.showCategory !== false;
  const showAuthor = p.showAuthor !== false;
  const ctaText = p.ctaText ?? "Read More";
  const baseUrl = p.baseUrl ?? "/blog";

  const headingHtml = `<h2 class="ease-blog-listing-heading">${text(heading)}</h2>`;

  const cardsHtml = posts
    .map((post) => renderBlogCard(post, baseUrl, ctaText, showCategory, showAuthor))
    .join("\n");

  return `<section class="ease-section ease-blog-listing-grid" aria-label="${attr(heading)}">
  ${headingHtml}
  <div class="ease-blog-grid" style="display: grid; grid-template-columns: repeat(${attr(String(columns))}, 1fr); gap: var(--ease-spacing-lg, 2rem);">
${cardsHtml}
  </div>
</section>`;
}

registerRenderer("blog-listing-grid", { render });
